import { useState, useEffect } from 'react';
import { captureAPI } from '@/lib/api';
import { Building2, CheckCircle2, Send } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent } from '@/components/ui/card';

export default function CaptureFormPage() {
  const [config, setConfig] = useState(null);
  const [form, setForm] = useState({});
  const [privacyAccepted, setPrivacyAccepted] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    captureAPI.getConfig().then(res => {
      setConfig(res.data);
      const defaults = {};
      (res.data.fields || []).forEach(f => { defaults[f.key] = ''; });
      setForm(defaults);
    }).catch(() => setError('Das Formular ist derzeit nicht verfügbar.'));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!privacyAccepted) {
      setError('Bitte bestätige die Datenschutzerklärung.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      await captureAPI.submit({ ...form, privacy_accepted: true, marketing_consent: marketingConsent });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || 'Etwas ist schiefgelaufen. Bitte versuche es erneut.');
    } finally {
      setSubmitting(false);
    }
  };

  if (error && !config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <p className="text-muted-foreground">{error}</p>
      </div>
    );
  }

  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!config.enabled) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4">
        <p className="text-muted-foreground">Dieses Formular ist derzeit deaktiviert.</p>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-4" data-testid="capture-success">
        <Card className="max-w-md w-full border-border/60">
          <CardContent className="p-8 text-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center mx-auto">
              <CheckCircle2 className="w-8 h-8 text-green-600 dark:text-green-400" />
            </div>
            <h2 className="text-xl font-bold">Vielen Dank!</h2>
            <p className="text-muted-foreground text-sm">
              Wir haben deine Anfrage erhalten und melden uns in Kürze bei dir.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/30 p-4" data-testid="capture-form-page">
      <Card className="max-w-lg w-full border-border/60 shadow-lg">
        <CardContent className="p-8 space-y-6">
          {/* Header */}
          <div className="text-center space-y-2">
            <div className="w-12 h-12 rounded-lg bg-primary flex items-center justify-center mx-auto">
              <Building2 className="w-6 h-6 text-primary-foreground" />
            </div>
            <h1 className="text-xl font-bold tracking-tight">{config.company_name || 'P1 Performance Center'}</h1>
            <p className="text-muted-foreground text-sm">Erzähl uns, wofür du dich interessierst — wir melden uns bei dir.</p>
          </div>

          {error && <p className="text-sm text-destructive text-center">{error}</p>}

          <form onSubmit={handleSubmit} className="space-y-4">
            {(config.fields || []).map((field) => (
              <div key={field.key} className="space-y-1.5">
                <Label className="text-sm">
                  {field.label} {field.required && <span className="text-destructive">*</span>}
                </Label>
                {field.type === 'textarea' ? (
                  <Textarea
                    value={form[field.key] || ''}
                    onChange={(e) => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                    required={field.required}
                    rows={3}
                    data-testid={`capture-${field.key}`}
                  />
                ) : field.type === 'select' ? (
                  <select value={form[field.key] || ''} onChange={(e) => setForm(p => ({ ...p, [field.key]: e.target.value }))} data-testid={`capture-${field.key}`} className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm">
                    <option value="">Bitte wählen...</option>
                    {(field.options || []).map(opt => (
                      <option key={opt} value={opt}>{opt}</option>
                    ))}
                  </select>
                ) : (
                  <Input
                    type={field.type || 'text'}
                    value={form[field.key] || ''}
                    onChange={(e) => setForm(p => ({ ...p, [field.key]: e.target.value }))}
                    required={field.required}
                    data-testid={`capture-${field.key}`}
                  />
                )}
              </div>
            ))}

            {/* DSGVO: Pflicht-Kenntnisnahme + optionale Marketing-Einwilligung */}
            <div className="space-y-3 pt-2 border-t border-border/60">
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={privacyAccepted}
                  onChange={(e) => setPrivacyAccepted(e.target.checked)}
                  required
                  className="mt-0.5 accent-primary"
                  data-testid="capture-privacy-checkbox"
                />
                <span>
                  Ich habe die{' '}
                  <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="underline text-foreground">
                    Datenschutzerklärung
                  </a>{' '}
                  zur Kenntnis genommen. <span className="text-destructive">*</span>
                </span>
              </label>
              <label className="flex items-start gap-2.5 text-xs text-muted-foreground cursor-pointer">
                <input
                  type="checkbox"
                  checked={marketingConsent}
                  onChange={(e) => setMarketingConsent(e.target.checked)}
                  className="mt-0.5 accent-primary"
                  data-testid="capture-marketing-checkbox"
                />
                <span>
                  Ich möchte per E-Mail/SMS über Angebote und Neuigkeiten informiert werden (optional, jederzeit widerrufbar).
                </span>
              </label>
            </div>

            <Button type="submit" className="w-full h-11 gap-2" disabled={submitting || !privacyAccepted} data-testid="capture-submit-btn">
              {submitting ? 'Wird gesendet...' : 'Anfrage senden'}
              <Send className="w-4 h-4" />
            </Button>
          </form>

          <p className="text-[10px] text-muted-foreground text-center">
            Deine Daten werden ausschließlich zur Bearbeitung deiner Anfrage verwendet.
            Details in der <a href="/datenschutz" className="underline">Datenschutzerklärung</a>.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
